/* eslint-disable */
// src/examples/Breadcrumbs/index.js

import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { breadcrumbRoutes } from "./breadcrumbs.routes";

function routePatternToRegex(pattern) {
  const escaped = pattern.replace(/\//g, "\\/");
  const withParams = escaped.replace(/:\w+/g, "[^\\/]+");
  return new RegExp(`^${withParams}$`, "i");
}

function fillParams(template, params) {
  return template.replace(/:(\w+)/g, (_, key) => params?.[key] ?? `:${key}`);
}

function extractParams(pattern, pathname) {
  const pSeg = pattern.split("/").filter(Boolean);
  const aSeg = pathname.split("/").filter(Boolean);
  const params = {};
  pSeg.forEach((seg, i) => {
    if (seg.startsWith(":")) params[seg.slice(1)] = aSeg[i];
  });
  return params;
}

function matchBestRoute(pathname) {
  let best = null;
  let bestLen = -1;

  for (const r of breadcrumbRoutes) {
    if (!r?.route) continue;
    const rx = routePatternToRegex(r.route);
    if (!rx.test(pathname)) continue;

    const len = r.route.split("/").filter(Boolean).length;
    if (len > bestLen) {
      best = r;
      bestLen = len;
    }
  }

  if (!best) return null;
  return { routeDef: best, params: extractParams(best.route, pathname) };
}

function getName(routeDef, params, pathname) {
  if (typeof routeDef?.name === "function") return routeDef.name({ params, pathname });
  return routeDef?.name || null;
}

function getCachedLocalName(localId) {
  try {
    return sessionStorage.getItem(`bc:local:${localId}`) || null;
  } catch {
    return null;
  }
}

function getResolvedLabel(routeDef, params, pathname) {
  let label = getName(routeDef, params, pathname);
  if (!label) return null;

  if (routeDef.route === "/local/:id" && params?.id) {
    const cached = getCachedLocalName(params.id);
    if (cached) label = cached;
  }

  return label;
}

function buildCrumbs(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  const crumbs = [];

  for (let i = 1; i <= segs.length; i++) {
    const subPath = `/${segs.slice(0, i).join("/")}`;
    const hit = matchBestRoute(subPath);
    if (!hit) continue;

    const { routeDef, params } = hit;
    if (routeDef.hide) continue;

    const label = getResolvedLabel(routeDef, params, subPath);
    if (!label) continue;

    const replaceN = Number(routeDef.replace || 0);
    if (replaceN > 0) {
      crumbs.splice(Math.max(crumbs.length - replaceN, 0), replaceN);
    }

    if (crumbs.length && crumbs[crumbs.length - 1].label === label) continue;

    crumbs.push({ to: subPath, label });
  }

  return crumbs;
}

function getParentPath(pathname) {
  const hit = matchBestRoute(pathname);
  const parentDef = hit?.routeDef?.parent;
  if (!hit || !parentDef) return null;

  let parentPath =
    typeof parentDef === "function"
      ? parentDef({ params: hit.params, pathname })
      : parentDef;

  if (!parentPath) return null;

  return fillParams(parentPath, hit.params);
}

function buildExplicitParentChain(pathname, seen = new Set()) {
  if (!pathname || seen.has(pathname)) return [];
  seen.add(pathname);

  const hit = matchBestRoute(pathname);
  if (!hit || hit.routeDef?.hide) return [];

  const label = getResolvedLabel(hit.routeDef, hit.params, pathname);
  if (!label) return [];

  const current = { to: pathname, label };
  const parentPath = getParentPath(pathname);
  if (!parentPath) return [current];

  return [...buildExplicitParentChain(parentPath, seen), current];
}

function getDynamicParentPath(pathname, locationState) {
  const hit = matchBestRoute(pathname);
  const currentRoute = hit?.routeDef?.route;
  const supportedRoutes = new Set([
    "/local/:id/recaudaciones/:visitId",
    "/local/:id/visitMachine/:visitId/editar",
  ]);

  if (!supportedRoutes.has(currentRoute)) return null;

  const candidate = locationState?.returnTo?.pathname;
  if (!candidate || candidate === pathname) return null;

  const parentHit = matchBestRoute(candidate);
  if (!parentHit || parentHit.routeDef?.hide) return null;

  return candidate;
}

function dedupeCrumbs(crumbs) {
  return crumbs.filter(
    (crumb, idx, arr) => arr.findIndex((item) => item.to === crumb.to) === idx
  );
}

function buildBreadcrumbTrail(pathname, locationState = null, seen = new Set()) {
  if (!pathname || seen.has(pathname)) return [];
  seen.add(pathname);

  const matchedCurrent = matchBestRoute(pathname);
  if (!matchedCurrent || matchedCurrent.routeDef?.hide) return buildCrumbs(pathname);

  const dynamicParentPath = getDynamicParentPath(pathname, locationState);
  if (dynamicParentPath) {
    const parentTrail = buildBreadcrumbTrail(dynamicParentPath, null, seen);
    const currentLabel = getResolvedLabel(matchedCurrent.routeDef, matchedCurrent.params, pathname);
    if (!currentLabel) return parentTrail;

    return dedupeCrumbs([...parentTrail, { to: pathname, label: currentLabel }]);
  }

  const hasExplicitParent = Boolean(matchedCurrent?.routeDef?.parent);

  if (!hasExplicitParent) return buildCrumbs(pathname);

  const explicitChain = buildExplicitParentChain(pathname);
  if (!explicitChain.length) return buildCrumbs(pathname);

  const rootPath = explicitChain[0]?.to;
  const prefixChain = rootPath ? buildCrumbs(rootPath) : [];

  return dedupeCrumbs([...prefixChain, ...explicitChain.slice(1)]);
}

function Breadcrumbs({ icon, title, light }) {
  const location = useLocation();
  const { pathname } = location;

  const crumbs = buildBreadcrumbTrail(pathname, location.state);
  const finalTitle = crumbs.length > 0 ? crumbs[crumbs.length - 1].label : title;
  const clickableWithParent = crumbs.length > 1 ? crumbs.slice(0, -1) : [];

  useEffect(() => {
    const l = location.state?.local;
    const id = l?.Id ?? l?.LocalId;
    const name = l?.Name;
    if (id && name) {
      sessionStorage.setItem(`bc:local:${id}`, name);
    }
  }, [location.state]);

  return (
    <MDBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600]),
          },
        }}
      >
        <Link to="/">
          <MDTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </MDTypography>
        </Link>

        {clickableWithParent.map((c) => (
          <Link to={c.to} key={c.to}>
            <MDTypography
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {c.label}
            </MDTypography>
          </Link>
        ))}

        <MDTypography
          variant="button"
          fontWeight="regular"
          textTransform={crumbs.length > 0 ? "none" : "capitalize"}
          color={light ? "white" : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {finalTitle}
        </MDTypography>
      </MuiBreadcrumbs>
    </MDBox>
  );
}

Breadcrumbs.defaultProps = { light: false };

Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  light: PropTypes.bool,
};

export default Breadcrumbs;
